import { Alert, Button, Drawer, Form, Grid, Input, Space } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { ApiError, applyFieldErrors } from "../app/apiError.ts";
import { emptyPerson, PersonFields, personBody, type PersonFormValues } from "./PersonFields.tsx";
import { ProfileTelegramLink } from "./TelegramLinkState.tsx";

type StaffMemberDetails = components["schemas"]["StaffMemberDetails"];

interface StaffForm extends PersonFormValues {
  position: string;
}

interface Props {
  open: boolean;
  /** Staff member being edited, or null to create one. */
  member: StaffMemberDetails | null;
  onClose(): void;
  onSaved(member: StaffMemberDetails, created: boolean): void;
}

export function StaffDrawer({ open, member, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<StaffForm>();
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  const mutation = useMutation({
    mutationFn: async (values: StaffForm) => {
      const body = { ...personBody(values), position: values.position.trim() };
      const result = member ? await api.PUT("/staff/{id}", { params: { path: { id: member.id } }, body }) : await api.POST("/staff", { body });
      if (!result.data) throw new ApiError(result.response.status, result.error);
      return result.data;
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ["staff"] });
      setServerErrors([]);
      onSaved(saved, !member);
    },
    onError: (err: unknown) => setServerErrors(applyFieldErrors(form, err, t("app.error"))),
  });

  const initialValues: StaffForm = member
    ? {
        lastName: member.lastName,
        firstName: member.firstName,
        middleName: member.middleName ?? "",
        phone: member.phone,
        additionalPhones: member.additionalPhones,
        email: member.email ?? "",
        position: member.position,
      }
    : { ...emptyPerson, position: "" };

  return (
    <Drawer
      title={member ? t("staff.edit") : t("staff.create")}
      open={open}
      onClose={onClose}
      size={screens.md ? 560 : "100%"}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="primary" loading={mutation.isPending} onClick={() => form.submit()}>
            {t("common.save")}
          </Button>
        </Space>
      }
    >
      {serverErrors.length > 0 && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} role="alert" title={serverErrors.map((m) => <div key={m}>{m}</div>)} />
      )}
      <Form<StaffForm> form={form} layout="vertical" requiredMark={false} initialValues={initialValues} onFinish={(values) => mutation.mutate(values)}>
        <Form.Item
          name="position"
          label={t("staff.position")}
          extra={t("staff.positionHint")}
          rules={[{ required: true, whitespace: true, message: t("person.validation.required") }]}
        >
          <Input maxLength={100} />
        </Form.Item>
        <PersonFields />
        {member && (
          <Form.Item label={t("telegram.label")}>
            <ProfileTelegramLink kind="StaffMember" profileId={member.id} name={member.fullName} canUnlink />
          </Form.Item>
        )}
      </Form>
    </Drawer>
  );
}
